/**
 * Captura de console e de rede.
 *
 * Instalada no carregamento do script, antes de qualquer interface: o erro que
 * explica o problema quase sempre acontece minutos antes de a pessoa decidir
 * reclamar. Se os ganchos só entrassem ao abrir o painel, chegariam tarde.
 *
 * O custo precisa ser desprezível, porque isto roda em toda página do cliente:
 * dois anéis de tamanho fixo, sem alocação por requisição bem-sucedida.
 */

import type { ConsoleEntry, NetworkEntry } from './types';

const MAX_CONSOLE_ENTRIES = 30;
const MAX_NETWORK_ENTRIES = 20;
const MAX_MESSAGE_LENGTH = 2000;

/** Parâmetros de consulta que costumam carregar credencial. */
const SENSITIVE_PARAMS = /^(token|access_token|api_key|apikey|key|secret|password|auth|sig|signature)$/i;

class RingBuffer<T> {
  private items: T[] = [];

  constructor(private readonly capacity: number) {}

  push(item: T): void {
    this.items.push(item);
    if (this.items.length > this.capacity) this.items.shift();
  }

  snapshot(): T[] {
    return [...this.items];
  }

  clear(): void {
    this.items = [];
  }
}

const consoleBuffer = new RingBuffer<ConsoleEntry>(MAX_CONSOLE_ENTRIES);
const networkBuffer = new RingBuffer<NetworkEntry>(MAX_NETWORK_ENTRIES);

let installed = false;

/**
 * Remove da URL o que possa ser credencial.
 *
 * A URL de uma requisição que falhou é útil para diagnosticar, mas leva query
 * string junto, e query string carrega token com frequência. O nome do
 * parâmetro fica; o valor vira `[removido]`.
 */
export function sanitizeUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl, window.location.href);
    url.searchParams.forEach((_, name) => {
      if (SENSITIVE_PARAMS.test(name)) url.searchParams.set(name, '[removido]');
    });
    // Fragmento nunca chega ao servidor e às vezes carrega token de sessão.
    url.hash = '';
    return url.toString();
  } catch {
    // URL relativa esquisita ou entrada inválida: melhor descartar o valor
    // inteiro do que arriscar registrar algo sensível sem tratamento.
    return '[url inválida]';
  }
}

function stringifyArgument(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    // Referência circular ou getter que lança.
    return String(value);
  }
}

function formatMessage(args: unknown[]): string {
  return args.map(stringifyArgument).join(' ').slice(0, MAX_MESSAGE_LENGTH);
}

function installConsoleHooks(): void {
  const levels: ConsoleEntry['level'][] = ['log', 'info', 'warn', 'error'];

  for (const level of levels) {
    const original = console[level].bind(console);
    console[level] = (...args: unknown[]) => {
      consoleBuffer.push({ level, message: formatMessage(args), timestamp: Date.now() });
      original(...args);
    };
  }

  // Erro não capturado não passa por console.error em todos os navegadores.
  window.addEventListener('error', (event) => {
    consoleBuffer.push({
      level: 'error',
      message: `${event.message} (${event.filename}:${event.lineno})`.slice(0, MAX_MESSAGE_LENGTH),
      timestamp: Date.now(),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    consoleBuffer.push({
      level: 'error',
      message: `Promise rejeitada: ${stringifyArgument(event.reason)}`.slice(0, MAX_MESSAGE_LENGTH),
      timestamp: Date.now(),
    });
  });
}

/** Só falhas entram no anel: registrar tudo encheria o buffer com ruído. */
function shouldRecord(status: number): boolean {
  return status === 0 || status >= 400;
}

function installFetchHook(): void {
  const originalFetch = window.fetch;
  if (typeof originalFetch !== 'function') return;

  window.fetch = async function patchedFetch(...args: Parameters<typeof fetch>) {
    const startedAt = Date.now();
    const [input, init] = args;
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const url = input instanceof Request ? input.url : String(input);

    try {
      const response = await originalFetch.apply(this, args);
      if (shouldRecord(response.status)) {
        networkBuffer.push({
          method,
          url: sanitizeUrl(url),
          status: response.status,
          duration_ms: Date.now() - startedAt,
          timestamp: startedAt,
        });
      }
      return response;
    } catch (error) {
      // Status 0 é a convenção para falha de rede, em que não houve resposta.
      networkBuffer.push({
        method,
        url: sanitizeUrl(url),
        status: 0,
        duration_ms: Date.now() - startedAt,
        timestamp: startedAt,
      });
      throw error;
    }
  };
}

interface TrackedXhr extends XMLHttpRequest {
  __clixyMethod?: string;
  __clixyUrl?: string;
  __clixyStart?: number;
}

function installXhrHook(): void {
  const OriginalXhr = window.XMLHttpRequest;
  if (typeof OriginalXhr !== 'function') return;

  const originalOpen = OriginalXhr.prototype.open;
  const originalSend = OriginalXhr.prototype.send;

  OriginalXhr.prototype.open = function patchedOpen(
    this: TrackedXhr,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    this.__clixyMethod = method.toUpperCase();
    this.__clixyUrl = String(url);
    // eslint-disable-next-line prefer-rest-params
    return originalOpen.apply(this, [method, url, ...rest] as never);
  };

  OriginalXhr.prototype.send = function patchedSend(this: TrackedXhr, ...args: unknown[]) {
    this.__clixyStart = Date.now();

    this.addEventListener('loadend', () => {
      if (!shouldRecord(this.status)) return;
      networkBuffer.push({
        method: this.__clixyMethod ?? 'GET',
        url: sanitizeUrl(this.__clixyUrl ?? ''),
        status: this.status,
        duration_ms: Date.now() - (this.__clixyStart ?? Date.now()),
        timestamp: this.__clixyStart ?? Date.now(),
      });
    });

    return originalSend.apply(this, args as never);
  };
}

/** Instala os ganchos uma vez só, mesmo que o script entre duas vezes na página. */
export function installBuffers(): void {
  if (installed) return;
  installed = true;

  installConsoleHooks();
  installFetchHook();
  installXhrHook();
}

export function getConsoleEntries(): ConsoleEntry[] {
  return consoleBuffer.snapshot();
}

export function getNetworkEntries(): NetworkEntry[] {
  return networkBuffer.snapshot();
}

export function clearBuffers(): void {
  consoleBuffer.clear();
  networkBuffer.clear();
}
